import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useCustomDispatch } from '../../hooks';
import { useAuthSelector } from '../../services/selectors/authSelector';
import { savePatientVitals } from '../../services/apis/dispense/vitalPatients';
import { SuccessMessage, ErrorMessage } from './AllConfirmationMessages';

const SimpleInput = ({ id, value, onChange, type = "text", placeholder = "", inputMode = "text", className = "", maxLength = null }) => {
	return (
		<input
			id={id}
			type={type}
			value={value}
			onChange={onChange}
			inputMode={inputMode}
			placeholder={placeholder}
			maxLength={maxLength}
			className={`w-full px-3 py-2.5 text-xs bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 ${className}`}
		/>
	);
};

const SimpleSelect = ({ id, value, onChange, options = [], placeholder = "Select", className = "" }) => {
	const hasWidth = className.includes('w-');
	return (
		<div className={`relative ${hasWidth ? '' : 'w-full'}`}>
			<select
				id={id}
				value={value}
				onChange={onChange}
				className={`${hasWidth ? '' : 'w-full'} px-3 py-2.5 pr-8 text-xs bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 appearance-none cursor-pointer ${className}`}
				style={{ textOverflow: 'clip' }}
			>
				<option value="" disabled>{placeholder}</option>
				{options.map((option) => (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				))}
			</select>
		</div>
	);
};

// const UnitLabel = ({ unit }) => {
// 	return (
// 		<span className="px-3 py-2.5 text-sm bg-gray-100 text-gray-600 rounded-lg border border-gray-300 whitespace-nowrap">
// 			{unit}
// 		</span>
// 	);
// };

const PillButton = ({ label, isSelected, onClick }) => {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`shrink-0 whitespace-nowrap px-1.5 py-1 text-[11px] leading-none font-medium rounded-full transition-colors ${isSelected
				? 'bg-red-600 text-white'
				: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
				}`}
		>
			{label}
		</button>
	);
};

const RadioButtonGroup = ({ id, value, onChange, options = [], className = "" }) => {
	return (
		<div className={`flex  ${className}`}>
			{options.map((option) => (
				<label
					key={option.value}
					htmlFor={`${id}-${option.value}`}
					className={`flex items-center gap-1 px-1 py-2.5 text-xs  cursor-pointer transition-colors ${
						value === option.value
							? 'text-blue-700'
							: 'text-gray-500'
					}`}
				>
					<input
						type="radio"
						id={`${id}-${option.value}`}
						name={id}
						value={option.value}
						checked={value === option.value}
						onChange={onChange}
						className="w-4 h-4 text-blue-600 focus:ring-blue-500 cursor-pointer focus:outline-none focus:ring-0"
					/>
					<span>{option.label}</span>
				</label>
			))}
		</div>
	);
};

const SectionHeading = ({ title, icon, badge }) => {
	return (
		<div className="flex items-center gap-2 mb-1.5">
			{icon && (
				<span style={{ display: 'inline-flex', alignItems: 'center' }}>
					{icon}
				</span>
			)}
			<h2 className="text-xs font-semibold uppercase text-gray-700">
				{title}
			</h2>
			{badge && <span className="ml-auto">{badge}</span>}
		</div>
	);
};

const StatusBadge = ({ status, type }) => {
	if (!status || (type === 'bp' && (status === 'Normal' || status === 'Elevated'))) {
		return null;
	}

	let colorClass = '';
	if (status === 'Fever') {
		colorClass = 'bg-red-100 text-red-800';
	} else if (status === 'Low') {
		colorClass = 'bg-blue-100 text-blue-800';
	} else if (status === 'High') {
		colorClass = 'bg-red-100 text-red-800';
	} else {
		colorClass = 'bg-blue-100 text-blue-800';
	}

	return (
		<span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${colorClass}`}>
			{status === 'Fever' && (
				<svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
					<path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" />
				</svg>
			)}
			{status}
		</span>
	);
};

const VitalSignsEntry = ({
	patientId,
	initialValues = {},
	onSuccess = null,
	onError = null,
	onRemovePatient = null,
	onClose = null,
	setShowErrorToast: externalSetShowErrorToast = null,
	isAddVitalsPage = false,
}) => {
	const dispatch = useCustomDispatch();
	const { pharmacyId } = useAuthSelector();

	const [loading, setLoading] = useState(false);
	const [validationError, setValidationError] = useState("");
	const [showSuccessToast, setShowSuccessToast] = useState(false);
	const [showErrorToast, setShowErrorToast] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const [autoSaveStatus, setAutoSaveStatus] = useState("idle");
	const debounceTimer = useRef(null);
	const vitalsRef = useRef(null);

	// Parse initial values to support both old and new formats
	const parseInitialValues = (vals) => {
		// If old format (bloodGlucose as string), try to parse
		if (vals.bloodGlucose && typeof vals.bloodGlucose === 'string') {
			const bgMatch = vals.bloodGlucose.match(/(\d+(?:\.\d+)?)\s*(mg\/dL|mmol\/L)?/i);
			if (bgMatch) {
				vals.bloodGlucoseValue = bgMatch[1];
				vals.bloodGlucoseUnit = bgMatch[2]?.toLowerCase() === 'mmol/l' ? 'mmol/L' : 'mg/dL';
			}
		}

		if (vals.bloodPressure && typeof vals.bloodPressure === 'string') {
			const bpMatch = vals.bloodPressure.match(/(\d+)\s*\/\s*(\d+)/);
			if (bpMatch) {
				vals.bpSystolic = bpMatch[1];
				vals.bpDiastolic = bpMatch[2];
			}
		}

		if (vals.temperature && typeof vals.temperature === 'string') {
			const tempMatch = vals.temperature.match(/(\d+(?:\.\d+)?)\s*[°]?[CF]?/i);
			if (tempMatch) {
				vals.temperatureValue = tempMatch[1];
				const unitMatch = vals.temperature.match(/[°]?([CF])/i);
				vals.temperatureUnit = unitMatch ? (unitMatch[1].toUpperCase() === 'F' ? '°F' : '°C') : '°C';
			}
		}

		if (vals.pulse && typeof vals.pulse === 'string') {
			const pulseMatch = vals.pulse.match(/(\d+)/);
			if (pulseMatch) {
				vals.heartRate = pulseMatch[1];
			}
		}

		if (vals.oxygenLevels && typeof vals.oxygenLevels === 'string') {
			const spo2Match = vals.oxygenLevels.match(/(\d+)/);
			if (spo2Match) {
				vals.spo2 = spo2Match[1];
			}
		}

		return vals;
	};

	const parsedInitials = parseInitialValues({ ...initialValues });

	const [vitals, setVitals] = useState({
		// Blood Glucose
		bloodGlucoseValue: parsedInitials.bloodGlucoseValue || "",
		bloodGlucoseUnit: parsedInitials.bloodGlucoseUnit || "mmol/L",
		timing: parsedInitials.timing || parsedInitials.glucoseTiming || "",

		// Blood Pressure
		bpSystolic: parsedInitials.bpSystolic || "",
		bpDiastolic: parsedInitials.bpDiastolic || "",
		bpArm: parsedInitials.bpArm || "Left",
		bpCuffSize: parsedInitials.bpCuffSize || "",

		// Heart Rate
		heartRate: parsedInitials.heartRate || parsedInitials.pulse || "",
		heartRhythm: parsedInitials.heartRhythm || "Regular",

		// Temperature
		temperatureValue: parsedInitials.temperatureValue || parsedInitials.temperature || "",
		temperatureUnit: parsedInitials.temperatureUnit || "°F",

		// Oxygen Saturation
		spo2: parsedInitials.spo2 || parsedInitials.oxygenLevels || "",
		oxygenDelivery: parsedInitials.oxygenDelivery || "Room Air",
		oxygenFlowRate: parsedInitials.oxygenFlowRate || "",

		// LPN Initials
		lpnInitials: parsedInitials.lpnInitials || "",
	});

	// Keep vitalsRef in sync with vitals state
	useEffect(() => {
		vitalsRef.current = vitals;
	}, [vitals]);

	// Auto-detect temperature unit if not set
	useEffect(() => {
		if (vitals.temperatureValue && !vitals.temperatureUnit) {
			const tempValue = parseFloat(vitals.temperatureValue);
			if (!isNaN(tempValue)) {
				setVitals(prev => ({
					...prev,
					temperatureUnit: tempValue >= 45 ? '°F' : '°C'
				}));
			}
		}
	}, [vitals.temperatureValue, vitals.temperatureUnit]);

	// Helper: Convert temperature to Celsius for storage
	const convertToCelsius = (value, unit) => {
		if (!value || !unit) return value;
		const numValue = parseFloat(value);
		if (isNaN(numValue)) return value;
		if (unit === '°F') {
			return ((numValue - 32) * 5 / 9).toFixed(2);
		}
		return numValue.toString();
	};

	// Helper: Detect BP status
	const detectBPStatus = (systolic, diastolic) => {
		const sys = parseFloat(systolic);
		const dia = parseFloat(diastolic);
		if (isNaN(sys) || isNaN(dia)) return null;

		if (sys < 90 || dia < 60) return 'Low';
		if (sys >= 140 || dia >= 90) return 'High';
		if (sys >= 120 || dia >= 80) return 'Elevated';
		return 'Normal';
	};

	// Helper: Detect fever
	const detectFever = (value, unit) => {
		if (!value) return false;
		const numValue = parseFloat(value);
		if (isNaN(numValue)) return false;
		const celsius = unit === '°F' ? (numValue - 32) * 5 / 9 : numValue;
		return celsius >= 38.0; // Fever threshold in Celsius
	};

	// Helper: Check if at least one field has a value
	const hasAtLeastOneField = useCallback((vitalsData = null) => {
		const currentVitals = vitalsData || vitalsRef.current || vitals;
		return currentVitals.bloodGlucoseValue?.trim() ||
			currentVitals.bpSystolic?.trim() ||
			currentVitals.bpDiastolic?.trim() ||
			currentVitals.heartRate?.trim() ||
			currentVitals.temperatureValue?.trim() ||
			currentVitals.spo2?.trim() ||
			currentVitals.lpnInitials?.trim();
	}, [vitals]);

	/* ---------------------------------------
	   Save API
	----------------------------------------*/

	const saveVitals = useCallback(() => {
		// Get the latest vitals from ref to avoid stale closure
		const currentVitals = vitalsRef.current || vitals;

		// Transform vitals to API format (structured section-wise)
		const transformVitalsForAPI = () => {
			const transformed = {};

			if (currentVitals.timing) {
				transformed.timing = currentVitals.timing;
			}

			// Blood Glucose
			if (currentVitals.bloodGlucoseValue) {
				const bgObj = {
					value: currentVitals.bloodGlucoseValue,
				};
				if (currentVitals.bloodGlucoseUnit) {
					bgObj.unit = currentVitals.bloodGlucoseUnit;
				}
				transformed.blood_glucose = bgObj;
			}

			// Blood Pressure
			if (currentVitals.bpSystolic || currentVitals.bpDiastolic) {
				const bpObj = {};
				if (currentVitals.bpSystolic) {
					bpObj.systolic = currentVitals.bpSystolic;
				}
				if (currentVitals.bpDiastolic) {
					bpObj.diastolic = currentVitals.bpDiastolic;
				}
				if (currentVitals.bpArm) {
					bpObj.arm = currentVitals.bpArm;
				}
				// if (currentVitals.bpCuffSize) {
				// 	bpObj.cuff_size = currentVitals.bpCuffSize;
				// }
				transformed.blood_pressure = bpObj;
			}

			// Heart Rate / Pulse
			if (currentVitals.heartRate) {
				const pulseObj = {
					rate: currentVitals.heartRate,
				};
				if (currentVitals.heartRhythm) {
					pulseObj.rhythm = currentVitals.heartRhythm;
				}
				transformed.pulse = pulseObj;
			}

			// Temperature
			if (currentVitals.temperatureValue) {
				const celsiusValue = convertToCelsius(currentVitals.temperatureValue, currentVitals.temperatureUnit);
				const tempObj = {
					value: celsiusValue,
					unit: currentVitals.temperatureUnit || '°C',
				};
				// Store original value if different from converted value
				if (currentVitals.temperatureUnit === '°F') {
					tempObj.original_value = currentVitals.temperatureValue;
					tempObj.original_unit = '°F';
				}
				transformed.temperature = tempObj;
			}

			// Oxygen Saturation
			if (currentVitals.spo2) {
				const oxygenObj = {
					spo2: currentVitals.spo2,
				};
				if (currentVitals.oxygenDelivery) {
					oxygenObj.delivery_method = currentVitals.oxygenDelivery;
				}
				if (currentVitals.oxygenFlowRate && currentVitals.oxygenDelivery && currentVitals.oxygenDelivery !== 'Room Air') {
					oxygenObj.flow_rate = currentVitals.oxygenFlowRate;
				}
				transformed.oxygen_levels = oxygenObj;
			}

			// LPN Initials
			// if (currentVitals.lpnInitials) {
			// 	transformed.lpn_initials = currentVitals.lpnInitials;
			// }

			return transformed;
		};

		if (!hasAtLeastOneField(currentVitals)) return;
		if (!patientId || !pharmacyId) return;

		if (!isAddVitalsPage) {
			setAutoSaveStatus("saving");
		}

		setLoading(true);

		const transformedVitals = transformVitalsForAPI();

		dispatch(
			savePatientVitals({
				pharmacy_id: pharmacyId,
				patient_id: patientId,
				vitals: [transformedVitals],
				setLoading,
				setErrorToast:
					typeof externalSetShowErrorToast === "function"
						? externalSetShowErrorToast
						: undefined,
			})
		)
			.then((res) => {
				if (res.type === "savePatientVitals/fulfilled") {
					onSuccess?.({ patientId, ...transformedVitals });
					onRemovePatient?.(patientId);
					onClose?.();

					if (!isAddVitalsPage) {
						setAutoSaveStatus("saved");
						setTimeout(() => {
							setAutoSaveStatus("idle");
						}, 1500);
					} else {
						setShowSuccessToast(true);
						setTimeout(() => setShowSuccessToast(false), 2000);
					}
				} else {
					const errorMsg = res.payload?.message || res.payload?.error || "Failed to save vitals. Please try again.";
					setErrorMessage(errorMsg);
					setShowErrorToast(true);
					setTimeout(() => {
						setShowErrorToast(false);
						setErrorMessage("");
					}, 3000);
					onError?.(res.payload);
					setAutoSaveStatus("idle");
				}
			})
			.catch((error) => {
				const errorMsg = error?.message || error?.response?.data?.message || "Failed to save vitals. Please try again.";
				setErrorMessage(errorMsg);
				setShowErrorToast(true);
				setTimeout(() => {
					setShowErrorToast(false);
					setErrorMessage("");
				}, 3000);
				setAutoSaveStatus("idle");
				onError?.(error);
			});
	}, [
		vitals,
		hasAtLeastOneField,
		patientId,
		pharmacyId,
		dispatch,
		isAddVitalsPage,
		onSuccess,
		onRemovePatient,
		onClose,
		onError,
		externalSetShowErrorToast,
	]);

	/* ---------------------------------------
	   Change handler
	----------------------------------------*/

	const handleChange = (field, value) => {
		setVitals((prev) => ({ ...prev, [field]: value }));
		if (validationError) setValidationError("");

		// Reset oxygen flow rate if delivery changes to Room Air
		if (field === 'oxygenDelivery' && value === 'Room Air') {
			setVitals((prev) => ({ ...prev, oxygenFlowRate: "" }));
		}

		if (!isAddVitalsPage) {
			clearTimeout(debounceTimer.current);
			debounceTimer.current = setTimeout(() => {
				saveVitals();
			}, 2000);
		}
	};

	const handleManualSave = () => {
		if (!hasAtLeastOneField()) {
			setValidationError("Please fill at least one field");
			return;
		}
		saveVitals();
	};

	/* ---------------------------------------
	   UI
	----------------------------------------*/

	const renderAutoSaveStatus = () => {
		if (isAddVitalsPage) return null;

		if (autoSaveStatus === "saving") {
			return <span className="text-xs text-gray-500">Saving…</span>;
		}

		if (autoSaveStatus === "saved") {
			return <span className="text-xs text-green-600">Saved ✓</span>;
		}

		return null;
	};

	const bpStatus = detectBPStatus(vitals.bpSystolic, vitals.bpDiastolic);
	const hasFever = detectFever(vitals.temperatureValue, vitals.temperatureUnit);

	return (
		<div className="my-2 border border-gray-300 rounded-md px-2 py-2 bg-white">
			<div className="flex items-center justify-between mb-1">
				<h1 className="text-sm font-semibold uppercase text-gray-700">
					ADD VITALS
				</h1>
				{renderAutoSaveStatus()}
			</div>

			<div className="space-y-1.5">
				{/* Timing Section */}
				<div className="px-1.5 py-1">
					<div className="flex flex-nowrap items-center gap-1.5 overflow-hidden">
						<PillButton
							label="FASTING"
							isSelected={vitals.timing === "Fasting"}
							onClick={() => handleChange("timing", vitals.timing === "Fasting" ? "" : "Fasting")}
						/>
						<PillButton
							label="PRE-MEAL"
							isSelected={vitals.timing === "Pre-meal"}
							onClick={() => handleChange("timing", vitals.timing === "Pre-meal" ? "" : "Pre-meal")}
						/>
						<PillButton
							label="POST-MEAL"
							isSelected={vitals.timing === "Post-meal"}
							onClick={() => handleChange("timing", vitals.timing === "Post-meal" ? "" : "Post-meal")}
						/>
						<PillButton
							label="RANDOM"
							isSelected={vitals.timing === "Random"}
							onClick={() => handleChange("timing", vitals.timing === "Random" ? "" : "Random")}
						/>
					</div>
				</div>

				{/* Blood Glucose Section */}
				<div className="bg-gray-50 rounded-lg p-1.5 border border-gray-200">
					<SectionHeading
						title="BLOOD GLUCOSE"
						icon={
							<svg className="w-4 h-4 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
								<path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0L12 2.69z" />
							</svg>
						}
					/>
					<div className="space-y-2">
						<div className="flex gap-2">
							<div className="relative flex-1 min-w-0">
								<SimpleInput
									id={`bg-value-${patientId}`}
									value={vitals.bloodGlucoseValue}
									onChange={(e) => handleChange("bloodGlucoseValue", e.target.value)}
									type="number"
									inputMode="decimal"
									placeholder="Value"
									className="w-full"
								/>
								<span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
									{vitals.bloodGlucoseUnit || "mmol/L"}
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* Blood Pressure Section */}
				<div className="bg-gray-50 rounded-lg p-1.5 border border-gray-200">
					<div className="flex gap-2 justify-between ">
						
							<SectionHeading
								title="BLOOD PRESSURE"
								icon={
									<svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
										<path strokeLinecap="round" strokeLinejoin="round" d="M3 12h2l1-5 2 10 2-8 1 3h2M19 12h-2l-1-5-2 10-2-8-1 3h-2" />
									</svg>
								}
							/>
						
						{(bpStatus === 'High' || bpStatus === 'Low') &&
						<div className="mb-1">
							 <StatusBadge status={bpStatus} type="bp" />
						</div>}
					</div>
					<div className="space-y-2">
						<div className="flex items-center gap-1">
							<SimpleInput
								id={`bp-systolic-${patientId}`}
								value={vitals.bpSystolic}
								onChange={(e) => handleChange("bpSystolic", e.target.value)}
								type="number"
								inputMode="numeric"
								placeholder="Systolic"
								className=""
							/>
							<SimpleInput
								id={`bp-diastolic-${patientId}`}
								value={vitals.bpDiastolic}
								onChange={(e) => handleChange("bpDiastolic", e.target.value)}
								type="number"
								inputMode="numeric"
								placeholder="Diastolic"
								className=""
							/>
							<div className="">
								<RadioButtonGroup
									id={`bp-arm-${patientId}`}
									value={vitals.bpArm}
									onChange={(e) => handleChange("bpArm", e.target.value)}
									options={[
										{ value: "Left", label: "Left" },
										{ value: "Right", label: "Right" },
									]}
								/>
							</div>
						</div>
					</div>
				</div>

				{/* Heart Rate Section */}
				<div className="bg-gray-50 rounded-lg p-1.5 border border-gray-200">
					<SectionHeading
						title="PULSE"
						icon={
							<svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 24 24">
								<path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
							</svg>
						}
					/>
					<div className="flex items-center gap-1">
						<div className="relative w-full">
							<SimpleInput
								id={`hr-rate-${patientId}`}
								value={vitals.heartRate}
								onChange={(e) => handleChange("heartRate", e.target.value)}
								type="number"
								inputMode="numeric"
								placeholder=""
								className="w-full"
							/>
							<span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
								BPM
							</span>
						</div>
						<div className="w-full">
							<RadioButtonGroup
								id={`hr-rhythm-${patientId}`}
								value={vitals.heartRhythm}
								onChange={(e) => handleChange("heartRhythm", e.target.value)}
								options={[
									{ value: "Regular", label: "Regular" },
									{ value: "Irregular", label: "Irregular" },
								]}
							/>
						</div>
					</div>
				</div>

				{/* Temperature Section */}
				<div className="bg-gray-50 rounded-lg p-1.5 border border-gray-200">
					<div className="flex  justify-between ">
						<SectionHeading
							title="TEMPERATURE"
							icon={
								<svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 24 24">
									<path d="M15 13V5c0-1.66-1.34-3-3-3S9 3.34 9 5v8c-1.21.91-2 2.37-2 4 0 2.76 2.24 5 5 5s5-2.24 5-5c0-1.63-.79-3.09-2-4zm-4-8c0-.55.45-1 1-1s1 .45 1 1h-1v1h1v2h-1v1h1v2h-2V5z" />
								</svg>
							}
						/>

						{vitals.temperatureValue && vitals.temperatureUnit && hasFever && (
							<div className="mb-1">
								<StatusBadge status="Fever" type="temp" />
							</div>
						)}
					</div>

					<div className="flex gap-2">
						<SimpleInput
							id={`temp-value-${patientId}`}
							value={vitals.temperatureValue}
							onChange={(e) => handleChange("temperatureValue", e.target.value)}
							type="number"
							inputMode="decimal"
							placeholder="Value"
							className="flex-1"
						/>
						<RadioButtonGroup
							id={`temp-unit-${patientId}`}
							value={vitals.temperatureUnit}
							onChange={(e) => handleChange("temperatureUnit", e.target.value)}
							options={[
									{ value: "°F", label: "°F" },
									{ value: "°C", label: "°C" },
								]}
						/>
					</div>

				</div>

				{/* Oxygen Saturation (SpO2) Section */}
				<div className="bg-gray-50 rounded-lg p-1.5 border border-gray-200">
					<SectionHeading
						title="OXYGEN SATURATION"
						icon={
							<svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
								<path d="M3 18h2v-5H3v5zm4 0h2v-8H7v8zm4 0h2v-3h-2v3zm4 0h2v-6h-2v6zm4 0h2v-10h-2v10z" />
							</svg>
						}
					/>
					<div className="space-y-2">
						<div className="flex gap-2 items-center">
							<div className="relative flex-1 min-w-0">
								<SimpleInput
									id={`spo2-value-${patientId}`}
									value={vitals.spo2}
									onChange={(e) => handleChange("spo2", e.target.value)}
									type="number"
									inputMode="numeric"
									placeholder=""
									className="w-full"
								/>
								<span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
									%
								</span>
							</div>
							<div className="flex-1 min-w-0">
								<SimpleSelect
									id={`spo2-delivery-${patientId}`}
									value={vitals.oxygenDelivery}
									onChange={(e) => handleChange("oxygenDelivery", e.target.value)}
									options={[
										{ value: "Room Air", label: "Room Air" },
										{ value: "Nasal Cannula", label: "Nasal Cannula" },
										{ value: "Mask", label: "Mask" },
									]}
									placeholder="Delivery"
								/>
							</div>
						</div>
						{vitals.spo2 && vitals.oxygenDelivery && vitals.oxygenDelivery !== 'Room Air' && (
							<SimpleInput
								id={`spo2-flow-${patientId}`}
								value={vitals.oxygenFlowRate}
								onChange={(e) => handleChange("oxygenFlowRate", e.target.value)}
								type="number"
								inputMode="decimal"
								placeholder="Flow Rate (L/min)"
							/>
						)}
					</div>
				</div>

				{/* LPN Initials */}
				{/* <div className="bg-gray-50 rounded-lg p-1.5 border border-gray-200">
					<SimpleInput
						id={`lpn-initials-${patientId}`}
						value={vitals.lpnInitials}
						onChange={(e) =>
							handleChange("lpnInitials", e.target.value.toUpperCase())
						}
						placeholder="LPN Initials"
						maxLength={10}
					/>
				</div> */}
			</div>

			{validationError && (
				<p className="text-xs text-red-600 mt-2">
					{validationError}
				</p>
			)}

			{isAddVitalsPage && (
				<button
					onClick={handleManualSave}
					disabled={loading}
					className="mt-2 w-full py-2 bg-[#10b981] text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{loading ? "Saving..." : "ADD VITALS"}
				</button>
			)}

			{showSuccessToast && (
				<SuccessMessage
					message="Vitals saved successfully"
					setShowToast={() => setShowSuccessToast(false)}
				/>
			)}

			{showErrorToast && (
				<ErrorMessage
					message={errorMessage || "Failed to save vitals. Please try again."}
					setShowToast={() => {
						setShowErrorToast(false);
						setErrorMessage("");
					}}
				/>
			)}
		</div>
	);
};

export default VitalSignsEntry;
